import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../../context/authcontext";
import { API_BASE_URL } from "../../../../environment";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { getLifecycleTypeName } from "../../../../utils/lifecycleUtils";

interface TenantUserStats {
  total_tenant_users: number;
  active_users_count: number;
  inactive_users_count: number;
  available_licenses: number;
  subscription_id: number;
  subscription_name: string;
  subscription_expiration_date: string;
  maximum_allowed_users_in_subscription: number;
}

interface SalesUserStats {
  companies: number;
  contacts: number;
  leads: number;
  pipelines: number;
  deals: number;
  tenant_id: string;
  sales_user_id: string;
}

interface SuperAdminStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
  };
  users: {
    total: number;
    active: number;
    inactive: number;
  };
}

const RowCards = () => {
  const { auth } = useContext(AuthContext);
  const [tenantStats, setTenantStats] = useState<TenantUserStats | null>(null);
  const [salesStats, setSalesStats] = useState<SalesUserStats | null>(null);
  const [superAdminStats, setSuperAdminStats] =
    useState<SuperAdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { selectedTenantId } = useContext(AuthContext);
  const salesUserId = auth?.user_id || auth?.id || "";

  const selectedTenant = auth?.tenants?.find(
    (t: any) => String(t.tenant_id) === String(selectedTenantId)
  );

  const isSuperAdmin =
    auth?.tenants?.some(
      (t: any) =>
        t.role_key?.toLowerCase() === "superadmin" ||
        t.role_key?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "superadmin" ||
        t.role_name?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "platform_analyst" ||
        t.role_name?.toLowerCase() === "platformanalyst" ||
          t.role_key?.toLowerCase() === "apiadmin" ||
          t.role_key?.toLowerCase() === "api_admin" ||
          t.role_name?.toLowerCase() === "apiadmin" ||
          t.role_name?.toLowerCase() === "api_admin" ||
        t.role_key?.toLowerCase() === "platform_analyst" ||
        t.role_key?.toLowerCase() === "platformanalyst"
    ) || false;

  let userRole = selectedTenant?.role_key || selectedTenant?.role_name || "";
  if (isSuperAdmin && !selectedTenantId) {
    const superAdminTenant = auth?.tenants?.find(
      (t: any) =>
        t.role_key?.toLowerCase() === "superadmin" ||
        t.role_key?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "superadmin" ||
        t.role_name?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "platform_analyst" ||
        t.role_name?.toLowerCase() === "platformanalyst" ||
          t.role_key?.toLowerCase() === "apiadmin" ||
          t.role_key?.toLowerCase() === "api_admin" ||
          t.role_name?.toLowerCase() === "apiadmin" ||
          t.role_name?.toLowerCase() === "api_admin" ||
        t.role_key?.toLowerCase() === "platform_analyst" ||
        t.role_key?.toLowerCase() === "platformanalyst"
    );
    userRole = superAdminTenant?.role_key || superAdminTenant?.role_name || "";
  }

  const isClientAdmin = userRole === "CLIENTADMIN";
  const isSalesUser = userRole === "SALESUSER";

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const authHeader = localStorage.getItem("authCred") || "";

        if (isSuperAdmin) {
          const response = await fetch(
            `${API_BASE_URL}/dashboard/system-stats/`,
            {
              headers: {
                Authorization: `Basic ${authHeader}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          setSuperAdminStats(data);
        } else if (isClientAdmin) {
          if (!selectedTenantId) {
            setError("No tenant ID found");
            setLoading(false);
            return;
          }

          const response = await fetch(
            `${API_BASE_URL}/lookup/tenant-user-stats/${selectedTenantId}/`,
            {
              headers: {
                Authorization: `Basic ${authHeader}`,
              },
            }
          );

          if (!response.ok) {
            if (response.status === 404) {
              setTenantStats(null);
              setLoading(false);
              return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          setTenantStats(data);
        } else if (isSalesUser && salesUserId) {
          if (!selectedTenantId) {
            setError("No tenant ID found");
            setLoading(false);
            return;
          }

          const response = await fetch(
            `${API_BASE_URL}/dashboard/sales-user-stats/?tenant_id=${selectedTenantId}&sales_user_id=${salesUserId}`,
            {
              headers: {
                Authorization: `Basic ${authHeader}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          setSalesStats(data);
        } else {
          setError("Invalid user role or missing user ID");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedTenantId, salesUserId, isClientAdmin, isSalesUser, isSuperAdmin]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getDaysUntilExpiry = (dateString: string) => {
    if (!dateString) return null;
    try {
      const expiryDate = new Date(dateString);
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const getSubscriptionStatus = (dateString: string) => {
    const daysUntilExpiry = getDaysUntilExpiry(dateString);
    if (daysUntilExpiry === null)
      return { status: "unknown", color: "text-secondary" };

    if (daysUntilExpiry < 0) {
      return { status: "expired", color: "text-danger" };
    } else if (daysUntilExpiry <= 7) {
      return { status: "expiring_soon", color: "text-warning" };
    } else {
      return { status: "active", color: "text-success" };
    }
  };

  if (loading) {
    return (
      <div className="row row-gap-3 mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  {isSuperAdmin
                    ? "Loading system stats..."
                    : isClientAdmin
                    ? "Loading subscription data..."
                    : "Loading sales data..."}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="row row-gap-3 mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-4">
              <i
                className="ti ti-alert-circle text-danger mb-3"
                style={{ fontSize: "2rem" }}
              ></i>
              <h6 className="text-danger">Error Loading Data</h6>
              <small className="text-muted">{error}</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isClientAdmin && !tenantStats) {
    return (
      <div className="row row-gap-3 mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-4">
              <i
                className="ti ti-building-off text-warning mb-3"
                style={{ fontSize: "2rem" }}
              ></i>
              <h4 className="text-warning">No Active Subscription</h4>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSalesUser && !salesStats) {
    return (
      <div className="row row-gap-3 mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-4">
              <i
                className="ti ti-database-off text-muted mb-3"
                style={{ fontSize: "2rem" }}
              ></i>
              <h6 className="text-muted">No Data Available</h6>
              <small className="text-muted">No sales data found</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSuperAdmin && superAdminStats) {
    return (
      <div className="row row-gap-3 mb-4">
        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Total Tenants</p>
                    <h2 className="mb-1 fs-16">
                      {superAdminStats.tenants.total}
                    </h2>
                    <p className="text-info mb-0 fs-13">
                      <i className="ti ti-building me-1" />
                      System wide tenants
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-primary border border-primary">
                    <i className="ti ti-building fs-16 text-primary" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-01.svg"
              alt="total-tenants-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Active Tenants</p>
                    <h2 className="mb-1 fs-16">
                      {superAdminStats.tenants.active}
                    </h2>
                    <p className="text-danger mb-0 fs-13">
                      <i className="ti ti-arrow-bar-down me-1" />
                      {superAdminStats.tenants.inactive} inactive tenants
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-success border border-success">
                    <i className="ti ti-building-store fs-16 text-success" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-02.svg"
              alt="active-tenants-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Total Users</p>
                    <h2 className="mb-1 fs-16">
                      {superAdminStats.users.total}
                    </h2>
                    <p className="text-info mb-0 fs-13">
                      <i className="ti ti-users me-1" />
                      All system users
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-warning border border-warning">
                    <i className="ti ti-users fs-16 text-warning" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-03.svg"
              alt="total-users-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Active Users</p>
                    <h2 className="mb-1 fs-16">
                      {superAdminStats.users.active}
                    </h2>
                    <p className="text-danger mb-0 fs-13">
                      <i className="ti ti-arrow-bar-down me-1" />
                      {superAdminStats.users.inactive} inactive users
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-info border border-info">
                    <i className="ti ti-user-check fs-16 text-info" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-04.svg"
              alt="active-users-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isClientAdmin && tenantStats) {
    const subscriptionStatus = getSubscriptionStatus(
      tenantStats.subscription_expiration_date
    );
    const daysUntilExpiry = getDaysUntilExpiry(
      tenantStats.subscription_expiration_date
    );

    return (
      <div className="row row-gap-3 mb-4">
        {/* Subscription Card */}
        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Subscription</p>
                    <h2 className="mb-1 fs-16">
                      {tenantStats.subscription_name}
                    </h2>
                    <p className={`mb-0 fs-13 ${subscriptionStatus.color}`}>
                      <i
                        className={`ti ${
                          subscriptionStatus.status === "active"
                            ? "ti-arrow-bar-up"
                            : subscriptionStatus.status === "expiring_soon"
                            ? "ti-alert-triangle"
                            : "ti-arrow-bar-down"
                        } me-1`}
                      />
                      {subscriptionStatus.status === "active" &&
                        daysUntilExpiry !== null &&
                        daysUntilExpiry > 0 &&
                        `${daysUntilExpiry} days left`}
                      {subscriptionStatus.status === "expiring_soon" &&
                        daysUntilExpiry !== null &&
                        `Expires in ${daysUntilExpiry} days`}
                      {subscriptionStatus.status === "expired" &&
                        "Subscription expired"}
                      {subscriptionStatus.status === "unknown" &&
                        "Unknown status"}
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-primary border border-primary">
                    <i className="ti ti-building fs-16 text-primary" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-01.svg"
              alt="subscription-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Total Users Card */}
        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Total Users</p>
                    <h2 className="mb-1 fs-16">
                      {tenantStats.total_tenant_users}
                    </h2>
                    <p className="text-success mb-0 fs-13">
                      <i className="ti ti-arrow-bar-up me-1" />
                      {tenantStats.maximum_allowed_users_in_subscription} max
                      allowed
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-success border border-success">
                    <i className="ti ti-users fs-16 text-success" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-02.svg"
              alt="users-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Active Users Card */}
        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Active Users</p>
                    <h2 className="mb-1 fs-16">
                      {tenantStats.active_users_count}
                    </h2>

                    <p className="text-danger mb-0 fs-13">
                      <i className="ti ti-arrow-bar-down me-1" />
                      {tenantStats.inactive_users_count} inactive users
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-warning border border-warning">
                    <i className="ti ti-user-check fs-16 text-warning" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-03.svg"
              alt="active-users-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Available Licenses Card */}
        <div className="col-xl-3 col-sm-6 d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Available Licenses</p>
                    <h2 className="mb-1 fs-16">
                      {tenantStats.available_licenses}
                    </h2>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-info border border-info">
                    <i className="ti ti-license fs-16 text-info" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-04.svg"
              alt="licenses-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isSalesUser && salesStats) {
    return (
      <div className="row row-gap-3 mb-4">
        {/* Companies Card */}
        <div className="col d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Companies</p>
                    <h2 className="mb-1 fs-16">{salesStats.companies}</h2>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-primary border border-primary">
                    <i className="ti ti-building fs-16 text-primary" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-01.svg"
              alt="companies-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Contacts Card */}
        <div className="col d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">Contacts</p>
                    <h2 className="mb-1 fs-16">{salesStats.contacts}</h2>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-success border border-success">
                    <i className="ti ti-user fs-16 text-success" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-02.svg"
              alt="contacts-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Leads Card */}
        <div className="col d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">{getLifecycleTypeName("LEAD")}</p>
                    <h2 className="mb-1 fs-16">{salesStats.leads}</h2>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-info border border-info">
                    <i className="ti ti-chart-arcs fs-16 text-info" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-03.svg"
              alt="leads-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Pipelines Card */}
        <div className="col d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">
                      {getLifecycleTypeName("PIPELINE")}
                    </p>
                    <h2 className="mb-1 fs-16">{salesStats.pipelines}</h2>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-info border border-info">
                    <i className="ti ti-git-branch fs-16 text-info" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-04.svg"
              alt="pipelines-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>

        {/* Deals Card */}
        <div className="col d-flex">
          <div className="card flex-fill mb-0 position-relative overflow-hidden">
            <div className="card-body position-relative z-1">
              <div className="d-flex align-items-start justify-content-between">
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <p className="fs-14 mb-1">{getLifecycleTypeName("DEAL")}</p>
                    <h2 className="mb-1 fs-16">{salesStats.deals}</h2>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="avatar avatar-md rounded-circle bg-soft-danger border border-danger">
                    <i className="ti ti-medal fs-16 text-danger" />
                  </span>
                </div>
              </div>
            </div>
            <ImageWithBasePath
              src="assets/img/icons/elemnt-02.svg"
              alt="deals-bg"
              className="img-fluid position-absolute top-0 Start-0"
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RowCards;
